import * as React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { Project, ProjectBillableType, ProjectStatus } from '../../types'
import NumberFormatter from '../../utilities/NumberFormatter'
import ProjectHelper from '../../helpers/ProjectHelper'
import RateHelper from '../../helpers/RateHelper'

const LABOUR_COLOR = '#1D9E75'
const EXPENSE_COLOR = '#5DCAA5'
const EARNED_VALUE_COLOR = '#378ADD'
const EARNED_VALUE_OVER_COLOR = '#D85A30'

const BG = '#F2F5F7'
const SURFACE = '#FFFFFF'
const SURFACE2 = '#E8EDF0'
const BORDER = '#D5DCE1'
const TEXT = '#111820'
const TEXT2 = '#4A5E6A'
const TEXT3 = '#8A9BA8'
const TRACK_BG = '#E0E7EB'

const Wrapper = styled.div`
  background: ${SURFACE};
  border-radius: 14px;
  border: 1px solid ${BORDER};
  padding: 1.75rem 2rem;
`

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1.25rem;
`

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const HeaderTop = styled.div`
  display: flex;
  align-items: center;
  gap: 9px;
`

const HeaderTitle = styled.span`
  font-size: 18px;
  font-weight: 600;
  letter-spacing: -0.01em;
`

const HeaderSub = styled.div`
  font-size: 14px;
  color: ${TEXT2};
`

const Divider = styled.div`
  height: 1px;
  background: ${BORDER};
  margin: 1.25rem 0;
`

const SectionLabel = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: ${TEXT3};
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-bottom: 10px;
`

const Grid3 = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 1.25rem;

  @media (max-width: 580px) {
    grid-template-columns: 1fr;
  }
`

const Card = styled.div`
  background: ${SURFACE2};
  border-radius: 9px;
  padding: 13px 15px;
  border: 1px solid ${BORDER};
`

const CardLabel = styled.p`
  font-size: 13px;
  color: ${TEXT2};
  margin-bottom: 5px;
`

const CardValue = styled.p`
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 1.1;
`

const CardSub = styled.p`
  font-size: 12px;
  color: ${TEXT3};
  font-weight: 500;
  margin-top: 4px;
`

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 1rem;
`

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  color: ${TEXT2};
`

const LegendDot = styled.div<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex-shrink: 0;
  background: ${p => p.color};
`

const LegendBudgetLine = styled.div`
  width: 2px;
  height: 14px;
  background: ${TEXT};
  opacity: 0.6;
  border-radius: 1px;
  flex-shrink: 0;
`

const Tracker = styled.div`
  position: relative;
`

const TrackerAnn = styled.div`
  position: relative;
  height: 36px;
`

const AnnCaret = styled.div`
  position: absolute;
  bottom: 0;
  width: 0;
  height: 0;
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid ${TEXT};
  transform: translateX(-50%);
  opacity: 0.75;
`

const BudgetPill = styled.div`
  position: absolute;
  top: 3px;
  white-space: nowrap;
  background: ${TEXT};
  color: ${SURFACE};
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.01em;
  padding: 4px 10px;
  border-radius: 20px;
`

const TrackerChart = styled.div`
  position: relative;
  padding: 10px 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const TrackerRow = styled.div`
  position: relative;
`

const RowTrack = styled.div`
  position: relative;
  height: 22px;
  background: ${TRACK_BG};
  border-radius: 5px;
  overflow: hidden;
`

const BarSeg = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
  display: flex;
  align-items: center;
  overflow: hidden;
`

const BarLabel = styled.span`
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
  padding-left: 8px;
  letter-spacing: -0.01em;
`

const BudgetLine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: ${TEXT};
  opacity: 0.7;
  pointer-events: none;
  border-radius: 1px;
`

const TrackerScale = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 8px;

  span {
    font-size: 12px;
    color: ${TEXT3};
  }
`

const AmountsSummary = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 1px;
  background: ${BORDER};
  border-radius: 9px;
  overflow: hidden;
  margin-top: 1rem;
  border: 1px solid ${BORDER};

  @media (max-width: 580px) {
    grid-template-columns: repeat(3, 1fr);
  }
`

const AmountCell = styled.div`
  background: ${SURFACE2};
  padding: 10px 8px;
  text-align: center;
`

const AmountValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  margin-bottom: 4px;
  letter-spacing: -0.02em;
`

const AmountLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${TEXT3};
`

const AmountDot = styled.span<{ color: string }>`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 2px;
  margin-right: 4px;
  vertical-align: middle;
  position: relative;
  top: -1px;
  background: ${p => p.color};
`

const AlertBox = styled.div<{ variant: 'info' | 'danger' }>`
  border-radius: 9px;
  padding: 11px 15px;
  border: 1px solid ${p => p.variant === 'danger' ? '#F2C4B4' : '#B6D4EF'};
  background: ${p => p.variant === 'danger' ? '#FEF0EC' : '#EAF3FC'};
`

const AlertTitle = styled.p<{ variant: 'info' | 'danger' }>`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 3px;
  color: ${p => p.variant === 'danger' ? '#912A11' : '#174F85'};
`

const AlertBody = styled.p<{ variant: 'info' | 'danger' }>`
  font-size: 13px;
  line-height: 1.6;
  color: ${p => p.variant === 'danger' ? '#7A3420' : '#1C5D99'};
`

const Breakdown = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;

  @media (max-width: 580px) {
    grid-template-columns: 1fr;
  }
`

const BkCard = styled.div`
  background: ${SURFACE2};
  border-radius: 9px;
  padding: 13px 15px;
  border: 1px solid ${BORDER};
`

const BkTitle = styled.p`
  font-size: 10px;
  font-weight: 600;
  color: ${TEXT3};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 11px;
`

const BkRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 5px 0;
  border-bottom: 1px solid ${BORDER};

  &:last-child {
    border-bottom: none;
  }
`

const BkRowTotal = styled(BkRow)`
  border-top: 1px solid ${BORDER};
  border-bottom: none;
  margin-top: 2px;
  padding-top: 9px;
`

const BkRowRef = styled(BkRow)`
  background: ${SURFACE};
  border-radius: 6px;
  border: none;
  padding: 5px 7px;
  margin: 4px -7px;
`

const BkKey = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${TEXT2};
`

const BkDot = styled.span<{ color: string; opacity?: number }>`
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 2px;
  margin-right: 5px;
  vertical-align: middle;
  background: ${p => p.color};
  opacity: ${p => p.opacity ?? 1};
`

const BkVal = styled.span`
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${TEXT};
`

const BkValOk = styled(BkVal)`
  color: #0F6E56;
`

const BkValInfo = styled(BkVal)`
  color: #185FA5;
`

const BkValDanger = styled(BkVal)`
  color: #C0321A;
`

interface IProps {
  project: Project
  currency: string
  numberFormat: string
}

const ProjectProfitability: React.FC<IProps> = ({ project, currency, numberFormat }) => {
  const { t } = useTranslation()
  const annRef = React.useRef<HTMLDivElement>(null)
  const pillRef = React.useRef<HTMLDivElement>(null)

  const budget = Number(project.budget) || 0
  const labourCosts = Number(project.labour_costs) || 0
  const expenseCosts = Number(project.expense_costs) || 0
  const totalHours = Number(project.total_hours) || 0
  const hourlyRate = RateHelper.getRateForProject('hourly', project) || 0

  const totalCost = labourCosts + expenseCosts
  const hoursAtRate = totalHours * hourlyRate
  const earnedValue = hoursAtRate + expenseCosts
  const margin = budget - totalCost
  const marginPct = budget > 0 ? (margin / budget) * 100 : 0
  const remainingRoom = budget - earnedValue
  const remainingRoomPct = budget > 0 ? (remainingRoom / budget) * 100 : 0

  const isOverBudget = earnedValue > budget
  const yieldGap = Math.abs(remainingRoom)
  const yieldGapPct = budget > 0 ? (yieldGap / budget) * 100 : 0
  const earnedValueColor = isOverBudget ? EARNED_VALUE_OVER_COLOR : EARNED_VALUE_COLOR
  const earnedValueLabelColor = isOverBudget ? '#FFF0EB' : '#EBF4FD'
  const alertVariant = isOverBudget ? 'danger' : 'info'

  const maxRawValue = Math.max(budget * 1.05, earnedValue, totalCost)
  const scaleMax = Math.ceil(maxRawValue / 1000) * 1000 || 1000

  const budgetPct = Math.min((budget / scaleMax) * 100, 100)

  React.useLayoutEffect(() => {
    const ann = annRef.current
    const pill = pillRef.current
    if (!ann || !pill) return

    const annW = ann.offsetWidth
    const pillW = pill.offsetWidth
    const lineX = annW * budgetPct / 100
    const pad = 4

    if (lineX < pillW / 2 + pad) {
      pill.style.left = `${Math.max(0, lineX - 6)}px`
      pill.style.transform = 'translateX(0)'
    } else if (lineX > annW - pillW / 2 - pad) {
      pill.style.left = `${lineX + 6}px`
      pill.style.transform = 'translateX(-100%)'
    } else {
      pill.style.left = `${budgetPct}%`
      pill.style.transform = 'translateX(-50%)'
    }
  }, [budgetPct])

  const fmt = (v: number) => NumberFormatter.formatCurrency(currency, numberFormat, v)
  const fmtR = (v: number) => NumberFormatter.formatCurrency(currency, numberFormat, Math.round(v))

  const subParts = [
    project.contact?.name,
    ProjectHelper.getProjectBillableTypeLabel(project.billable_type),
    hourlyRate > 0 ? `${fmt(hourlyRate)}/h` : null,
  ].filter(Boolean)

  const statusAlert = (() => {
    switch (project.status) {
      case ProjectStatus.PROPOSAL:
        return {
          title: t('ProjectProfitability::Proposal — projected margin {{pct}}%', { pct: marginPct.toFixed(1) }),
          body: t('ProjectProfitability::This project is still a proposal. Projected value is {{earnedValue}} on a budget of {{budget}}, leaving {{room}}.', {
            earnedValue: fmtR(earnedValue),
            budget: fmtR(budget),
            room: fmtR(remainingRoom),
          }),
        }
      case ProjectStatus.ON_HOLD:
        return {
          title: t('ProjectProfitability::Project on hold — current margin {{pct}}%', { pct: marginPct.toFixed(1) }),
          body: t('ProjectProfitability::This project is on hold. So far {{earnedValue}} in earned value on a budget of {{budget}}, with {{room}} remaining.', {
            earnedValue: fmtR(earnedValue),
            budget: fmtR(budget),
            room: fmtR(remainingRoom),
          }),
        }
      case ProjectStatus.COMPLETED:
        return {
          title: t('ProjectProfitability::Project completed — final margin {{pct}}%', { pct: marginPct.toFixed(1) }),
          body: t('ProjectProfitability::{{earnedValue}} earned on a budget of {{budget}}. Final margin is {{margin}} ({{pct}}%).', {
            earnedValue: fmtR(earnedValue),
            budget: fmtR(budget),
            margin: fmtR(margin),
            pct: marginPct.toFixed(1),
          }),
        }
      case ProjectStatus.CANCELLED:
        return {
          title: t('ProjectProfitability::Project cancelled — margin {{pct}}%', { pct: marginPct.toFixed(1) }),
          body: t('ProjectProfitability::This project was cancelled. {{earnedValue}} in earned value was recorded against a budget of {{budget}}.', {
            earnedValue: fmtR(earnedValue),
            budget: fmtR(budget),
          }),
        }
      case ProjectStatus.ACTIVE:
      default:
        return {
          title: t('ProjectProfitability::Project running — current margin {{pct}}%', { pct: marginPct.toFixed(1) }),
          body: t('ProjectProfitability::So far {{earnedValue}} in earned value on a budget of {{budget}}. There is {{room}} remaining — but more hours will likely be tracked.', {
            earnedValue: fmtR(earnedValue),
            budget: fmtR(budget),
            room: fmtR(remainingRoom),
          }),
        }
    }
  })()

  return (
    <Wrapper>

      <Header>
        <HeaderLeft>
          <HeaderTop>
            <HeaderTitle>{project.name}</HeaderTitle>
          </HeaderTop>
          {subParts.length > 0 && (
            <HeaderSub>{subParts.join(' · ')}</HeaderSub>
          )}
        </HeaderLeft>
      </Header>

      <Divider style={{ marginTop: 0 }} />

      <SectionLabel>{t('ProjectProfitability::Inputs')}</SectionLabel>
      <Grid3>
        <Card>
          <CardLabel>{t('ProjectProfitability::Offered budget')}</CardLabel>
          <CardValue>{fmt(budget)}</CardValue>
          <CardSub>
            {project.billable_type === ProjectBillableType.FIXED_BUDGET
              ? t('ProjectProfitability::fixed price quote')
              : t('ProjectProfitability::project budget')}
          </CardSub>
        </Card>
        <Card>
          <CardLabel>{t('ProjectProfitability::Tracked hours')}</CardLabel>
          <CardValue>{NumberFormatter.formatNumber(numberFormat, totalHours)}h</CardValue>
          <CardSub>{t('ProjectProfitability::via time tracking')}</CardSub>
        </Card>
        <Card>
          <CardLabel>{t('ProjectProfitability::Expenses')}</CardLabel>
          <CardValue>{fmt(expenseCosts)}</CardValue>
          <CardSub>{t('ProjectProfitability::entered in Bizzey')}</CardSub>
        </Card>
      </Grid3>

      <Divider />

      <SectionLabel>{t('ProjectProfitability::Budget tracker')}</SectionLabel>

      <Legend>
        <LegendItem>
          <LegendDot color={LABOUR_COLOR} />
          {t('ProjectProfitability::labour at cost')}
        </LegendItem>
        <LegendItem>
          <LegendDot color={EXPENSE_COLOR} />
          {t('ProjectProfitability::+ expenses')}
        </LegendItem>
        <LegendItem>
          <LegendDot color={earnedValueColor} />
          {isOverBudget && t('ProjectProfitability::earned value @ tariff')}
          {!isOverBudget && t('ProjectProfitability::earned value @ rate (to date)')}
        </LegendItem>
        <LegendItem style={{ gap: '6px' }}>
          <LegendBudgetLine /> {t('ProjectProfitability::budget / invoice')}
        </LegendItem>
      </Legend>

      <Tracker>
        <TrackerAnn ref={annRef}>
          <AnnCaret style={{ left: `${budgetPct}%` }} />
          <BudgetPill ref={pillRef} style={{ left: `${budgetPct}%`, transform: 'translateX(-50%)' }}>
            {fmtR(budget)}
          </BudgetPill>
        </TrackerAnn>

        <TrackerChart>
          <TrackerRow>
            <RowTrack>
              <BarSeg
                style={{
                  width: `${Math.min((labourCosts / scaleMax) * 100, 100)}%`,
                  background: LABOUR_COLOR,
                }}
              >
                <BarLabel style={{ color: '#D1FAE5' }}>
                  {fmtR(labourCosts)} {t('ProjectProfitability::labour')}
                </BarLabel>
              </BarSeg>
              <BarSeg
                style={{
                  left: `${Math.min((labourCosts / scaleMax) * 100, 100)}%`,
                  width: `${Math.min((expenseCosts / scaleMax) * 100, 100)}%`,
                  background: EXPENSE_COLOR,
                  borderRadius: '0 4px 4px 0',
                }}
              >
                <BarLabel style={{ color: '#064E3B' }}>
                  +{fmtR(expenseCosts)}
                </BarLabel>
              </BarSeg>
            </RowTrack>
          </TrackerRow>

          <TrackerRow>
            <RowTrack>
              <BarSeg
                style={{
                  width: `${Math.min((earnedValue / scaleMax) * 100, 100)}%`,
                  background: earnedValueColor,
                  borderRadius: '0 4px 4px 0',
                }}
              >
                <BarLabel style={{ color: earnedValueLabelColor }}>
                  {fmtR(earnedValue)} {t('ProjectProfitability::earned value')}
                </BarLabel>
              </BarSeg>
            </RowTrack>
          </TrackerRow>

          <BudgetLine style={{ left: `${budgetPct}%` }} />
        </TrackerChart>

        <TrackerScale>
          <span>{fmtR(0)}</span>
          <span>{fmtR(scaleMax)}</span>
        </TrackerScale>
      </Tracker>

      <AmountsSummary>
        <AmountCell>
          <AmountValue>
            <AmountDot color={LABOUR_COLOR} />{fmtR(labourCosts)}
          </AmountValue>
          <AmountLabel>{t('ProjectProfitability::labour')}</AmountLabel>
        </AmountCell>
        <AmountCell>
          <AmountValue>
            <AmountDot color={EXPENSE_COLOR} />{fmtR(expenseCosts)}
          </AmountValue>
          <AmountLabel>{t('ProjectProfitability::expenses')}</AmountLabel>
        </AmountCell>
        <AmountCell>
          <AmountValue>{fmtR(totalCost)}</AmountValue>
          <AmountLabel>{t('ProjectProfitability::total cost')}</AmountLabel>
        </AmountCell>
        <AmountCell>
          <AmountValue style={{ color: '#0F6E56' }}>{fmtR(margin)}</AmountValue>
          <AmountLabel>{t('ProjectProfitability::margin {{pct}}%', { pct: marginPct.toFixed(1) })}</AmountLabel>
        </AmountCell>
        <AmountCell>
          <AmountValue>
            <AmountDot color={earnedValueColor} />{fmtR(earnedValue)}
          </AmountValue>
          <AmountLabel>{t('ProjectProfitability::value')}</AmountLabel>
        </AmountCell>
        <AmountCell>
          {isOverBudget ? (
            <>
              <AmountValue style={{ color: '#C0321A' }}>-{fmtR(yieldGap)}</AmountValue>
              <AmountLabel>{t('ProjectProfitability::yield gap')}</AmountLabel>
            </>
          ) : (
            <>
              <AmountValue style={{ color: '#185FA5' }}>+{fmtR(remainingRoom)}</AmountValue>
              <AmountLabel>{t('ProjectProfitability::room left')}</AmountLabel>
            </>
          )}
        </AmountCell>
      </AmountsSummary>

      <Divider />

      <AlertBox variant={alertVariant}>
        <AlertTitle variant={alertVariant}>
          {isOverBudget
            ? t('ProjectProfitability::Critical yield — yield gap {{gap}}', { gap: fmtR(yieldGap) })
            : statusAlert.title}
        </AlertTitle>
        <AlertBody variant={alertVariant}>
          {isOverBudget
            ? t('ProjectProfitability::{{earnedValue}} in value delivered on an invoice of {{budget}}. Internal cost is {{cost}}, margin only {{pct}}%.', {
              earnedValue: fmtR(earnedValue),
              budget: fmtR(budget),
              cost: fmtR(totalCost),
              pct: marginPct.toFixed(1),
            })
            : statusAlert.body}
        </AlertBody>
      </AlertBox>

      <Divider />

      <SectionLabel>{t('ProjectProfitability::Breakdown')}</SectionLabel>
      <Breakdown>
        <BkCard>
          <BkTitle>{t('ProjectProfitability::Cost side')}</BkTitle>
          <BkRow>
            <BkKey>
              <BkDot color={LABOUR_COLOR} />
              {t('ProjectProfitability::Labour ({{hours}}h)', { hours: NumberFormatter.formatNumber(numberFormat, totalHours) })}
            </BkKey>
            <BkVal>{fmt(labourCosts)}</BkVal>
          </BkRow>
          <BkRow>
            <BkKey>
              <BkDot color={EXPENSE_COLOR} />
              {t('ProjectProfitability::Expenses')}
            </BkKey>
            <BkVal>{fmt(expenseCosts)}</BkVal>
          </BkRow>
          <BkRowTotal>
            <BkKey>{t('ProjectProfitability::Total cost')}</BkKey>
            <BkVal>{fmt(totalCost)}</BkVal>
          </BkRowTotal>
          <BkRowRef>
            <BkKey style={{ fontWeight: 500, color: TEXT }}>{t('ProjectProfitability::Budget (invoice)')}</BkKey>
            <BkVal>{fmt(budget)}</BkVal>
          </BkRowRef>
          <BkRow>
            <BkKey>{t('ProjectProfitability::Current margin')}</BkKey>
            <BkValOk>{fmt(margin)} / {marginPct.toFixed(1)}%</BkValOk>
          </BkRow>
        </BkCard>

        <BkCard>
          <BkTitle>{t('ProjectProfitability::Value side')}</BkTitle>
          <BkRow>
            <BkKey>
              <BkDot color={earnedValueColor} />
              {t('ProjectProfitability::Hours @ {{rate}}', { rate: fmt(hourlyRate) })}
            </BkKey>
            <BkVal>{fmt(hoursAtRate)}</BkVal>
          </BkRow>
          <BkRow>
            <BkKey>
              <BkDot color={earnedValueColor} opacity={0.4} />
              {t('ProjectProfitability::Expenses')}
            </BkKey>
            <BkVal>{fmt(expenseCosts)}</BkVal>
          </BkRow>
          <BkRowTotal>
            <BkKey>{t('ProjectProfitability::Total value')}</BkKey>
            <BkVal>{fmt(earnedValue)}</BkVal>
          </BkRowTotal>
          <BkRowRef>
            <BkKey style={{ fontWeight: 500, color: TEXT }}>{t('ProjectProfitability::Budget (invoice)')}</BkKey>
            <BkVal>{fmt(budget)}</BkVal>
          </BkRowRef>
          {isOverBudget ? (
            <BkRow>
              <BkKey>{t('ProjectProfitability::Yield gap')}</BkKey>
              <BkValDanger>-{fmt(yieldGap)} / -{Math.round(yieldGapPct)}%</BkValDanger>
            </BkRow>
          ) : (
            <BkRow>
              <BkKey>{t('ProjectProfitability::Remaining room')}</BkKey>
              <BkValInfo>+{fmt(remainingRoom)} / +{Math.round(remainingRoomPct)}%</BkValInfo>
            </BkRow>
          )}
        </BkCard>
      </Breakdown>
    </Wrapper>
  )
}

export default ProjectProfitability
